<template>
  <div class="ship-pick ship-scope">
    <Spinner v-if="loading"
             size="sm" />

    <div v-else-if="failed"
         class="alert alert-warning d-flex align-items-center flex-wrap mb-0 unavailable">
      <span><i class="fal fa-exclamation-triangle mr-1"></i>{{ $t("messenger.ship.choice.load_failed") }}</span>
      <button type="button"
              class="btn btn-outline-primary btn-sm ml-auto"
              @click="init()">{{ $t("messenger.retry") }}</button>
    </div>

    <template v-else>
      <div class="ship-pick-page">
        <PageAvatar :page="page" />
        <b>{{ page.name }}</b>
      </div>
      <h4 class="font-weight-bold text-color-2">{{ $t("messenger.ship.choice.title") }}</h4>
      <p class="muted-note mt-1">{{ $t("messenger.ship.choice.sub") }}</p>

      <div class="ship-pick-list mt-3">
        <button v-for="shop in shops"
                :key="shop.id"
                type="button"
                class="ship-pick-btn"
                :disabled="saving != null"
                @click="pick(shop)">
          <b>
            <i v-if="saving === shop.id"
               class="fal fa-spinner-third fa-spin mr-1"></i>{{ shop.name }}
          </b>
          <span v-if="shop.sub">{{ shop.sub }}</span>
        </button>
      </div>

      <div v-if="saveError"
           class="save-error">
        <i class="fal fa-exclamation-circle mr-1"></i>{{ saveError }}
      </div>

      <router-link :to="{ name: 'Seller-Settings-Shipping' }"
                   class="btn btn-outline-primary mt-3">{{ $t("messenger.ship.choice.back") }}</router-link>
    </template>
  </div>
</template>

<script>
import Spinner from "@/components/core/Spinner";
import PageAvatar from "@/components/derived/messenger/PageAvatar";
import { takeClaimShops } from "@/store/modules/messenger";
import { escapeHtml } from "@/components/derived/messenger/format";
import { Toast } from "@/helpers/toastr";

// เพจนี้ขายของร้านไหน? One screen per Page: after the connect flow for every needs_choice Page
// (shops stashed from the claim response), and for เลือกร้าน (the seller's shops).
// Route: /settings/shipping/shop/:pageId with ?next=<pageId,pageId> while Pages remain.
export default {
  name: "ShopChoice",
  components: { Spinner, PageAvatar },

  data() {
    return {
      loading: true,
      failed: false,
      page: { pageId: "", name: "", pictureUrl: null },
      shops: [], // [{id, name, sub}]
      saving: null, // shop id being saved
      saveError: null,
    };
  },

  created() {
    this.init();
  },

  watch: {
    "$route.params.pageId"() {
      this.init();
    },
  },

  methods: {
    init() {
      const pageId = String((this.$route && this.$route.params && this.$route.params.pageId) || "");
      this.loading = true;
      this.failed = false;
      this.saving = null;
      this.saveError = null;
      this.shops = [];
      this.page = { pageId, name: pageId, pictureUrl: null };

      const claimed = takeClaimShops(pageId);
      if (claimed && claimed.name) {
        this.page.name = claimed.name;
        this.page.pictureUrl = claimed.pictureUrl || null;
      }

      let pending = claimed ? 1 : 2;
      let pages = null;
      let sellerShops = null; // shops store entries: [{id, details:{name}}]
      let failed = false;
      const settle = () => {
        pending -= 1;
        if (pending > 0) return;
        if (this._isDestroyed) return;
        if (failed) {
          this.loading = false;
          this.failed = true;
          return;
        }
        const found = (pages || []).find((page) => String(page.pageId) === pageId);
        if (found) {
          this.page = { pageId, name: found.name, pictureUrl: found.pictureUrl };
        }
        if (claimed) {
          this.shops = claimed.shops.map((shop) => ({ id: shop.id, name: shop.name, sub: shop.sub || "" }));
        } else if (sellerShops) {
          this.shops = sellerShops.map((shop) => ({
            id: shop.id,
            name: (shop.details && shop.details.name) || String(shop.id),
            sub: "",
          }));
        }
        if (!this.shops.length) {
          this.failed = true;
        }
        this.loading = false;
      };

      this.$store.dispatch("messenger/fetchPages", {
        callback: (ok, data) => {
          if (!ok) failed = true;
          else pages = data;
          settle();
        },
      });
      if (!claimed) {
        this.$store.dispatch("shops/fetchShops", {
          query: "responseSize=all",
          callback: (ok, data) => {
            if (!ok || !Array.isArray(data)) failed = true;
            else sellerShops = data;
            settle();
          },
        });
      }
    },

    pick(shop) {
      if (this.saving != null) return;
      this.saving = shop.id;
      this.saveError = null;
      this.$store.dispatch("messenger/setPageShop", {
        pageId: this.page.pageId,
        shopId: shop.id,
        callback: (ok, response) => {
          this.saving = null;
          if (this._isDestroyed) return;
          if (!ok) {
            this.saveError = this.$t("messenger.ship.choice.save_failed");
            return;
          }
          const linked = response && Array.isArray(response.shops) && response.shops[0] ? response.shops[0] : null;
          Toast(
            this.$t("messenger.ship.choice.saved_toast", { name: escapeHtml(this.page.name), shop: escapeHtml((linked && linked.name) || shop.name) }),
            "success"
          );
          const query = (this.$route && this.$route.query) || {};
          const next = String(query.next || "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
          if (next.length) {
            this.$router.replace({
              name: "Seller-Settings-Shipping-Shop",
              params: { pageId: next[0] },
              query: next.length > 1 ? { next: next.slice(1).join(",") } : {},
            });
            return;
          }
          this.$router.push({ name: "Seller-Settings-Shipping" });
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ship-scope .btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ship-pick {
  max-width: 620px;
  margin: 0 auto;
}

.ship-pick-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ship-pick-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.15rem;
  width: 100%;
  min-height: 64px;
  padding: 1rem 1.1rem;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  color: #333;

  &:hover:not(:disabled) {
    border-color: $brand-blue-mid;
  }

  &:disabled {
    opacity: 0.65;
    cursor: default;
  }

  b {
    font-size: 1.1rem;
    color: #333;
  }

  span {
    color: #aaa;
    font-size: 0.875rem;
  }
}

.ship-pick-page {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.8rem;

  b {
    color: map-get($text-colors, 2);
    font-size: 1.05rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.save-error {
  color: #cf3a3a;
  font-size: 0.875rem;
  margin-top: 0.75rem;
}

.unavailable {
  gap: 0.5rem;
}

.muted-note {
  color: map-get($text-colors, 5);
  font-size: 0.875rem;
}

body:not(.--dark) {
  .ship-pick-btn span {
    color: #666;
  }

  .ship-scope .muted-note {
    color: #767676;
  }
}

body.--dark {
  .ship-pick-btn {
    background: #4b4b4b;
    border-color: #555;

    b {
      color: lightgray;
    }
  }

  .ship-scope .btn-outline-primary {
    color: #8cc8f5;
    border-color: #8cc8f5;
  }

  .ship-scope .btn-outline-primary:hover:not(:disabled) {
    color: #fff;
  }

  .save-error {
    color: #ff9d9d;
  }
}
</style>
